import { useEffect, useState, useCallback } from 'react';
import { getPracticeMetricsSummary } from 'api/marketing';
import { IPracticeMetricsQuery, IPracticeMetricsSummary, IAdMetricsDisplay } from 'types/marketing';
import { centsToYuan } from 'utils/marketing';

function convertToDisplay(metrics: IPracticeMetricsSummary): IAdMetricsDisplay {
    const impressionShareRate =
        metrics.auctionParticipants && metrics.totalImpressions
            ? Math.round((metrics.totalImpressions / metrics.auctionParticipants) * 100)
            : 0;
    const previousImpressionShareRate =
        metrics.previousAuctionParticipants && metrics.previousTotalImpressions
            ? Math.round((metrics.previousTotalImpressions / metrics.previousAuctionParticipants) * 100)
            : undefined;

    return {
        totalBookings: metrics.totalBookings || 0,
        adBookings: metrics.adBookings,
        totalImpressions: metrics.totalImpressions || 0,
        totalSpend: centsToYuan(metrics.totalSpendCents),
        auctionParticipants: metrics.auctionParticipants,
        previousTotalBookings: metrics.previousTotalBookings,
        previousAdBookings: metrics.previousAdBookings,
        previousTotalImpressions: metrics.previousTotalImpressions,
        previousImpressionShareRate,
        impressionShareRate,
        totalBookingsChangeRate: metrics.totalBookingsRate != null ? Math.round(metrics.totalBookingsRate) : undefined,
        impressionsChangeRate:
            metrics.totalImpressionsRate != null ? Math.round(metrics.totalImpressionsRate) : undefined,
        bookingsChangeRate:
            metrics.adBookings && metrics.previousAdBookings
                ? Math.round(((metrics.adBookings - metrics.previousAdBookings) / metrics.previousAdBookings) * 100)
                : undefined,
        impressionShareChangeRate:
            previousImpressionShareRate && impressionShareRate
                ? Math.round(((impressionShareRate - previousImpressionShareRate) / previousImpressionShareRate) * 100)
                : undefined,
        joinedAt: metrics.joinedAt,
    };
}

const usePracticeMetrics = (
    initialQuery: IPracticeMetricsQuery,
): [IAdMetricsDisplay, boolean, (params: IPracticeMetricsQuery) => void] => {
    const [loading, setLoading] = useState(false);
    const [metrics, setMetrics] = useState<IAdMetricsDisplay>({});

    const fetchData = useCallback(async (params: IPracticeMetricsQuery) => {
        try {
            setLoading(true);
            const result = await getPracticeMetricsSummary(params);
            if (!result.error && result?.data?.data) {
                setMetrics(convertToDisplay(result.data.data));
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(initialQuery);
    }, []);

    return [metrics, loading, fetchData];
};

export default usePracticeMetrics;
